import { createAtom } from "@tanstack/react-store";
import { createRowMotionTracker } from "../motion/rowMotionTracker";
import { isEditableTarget } from "./editableTarget";
import { getSoleControl } from "./focusLevel";
import type {
    DragSource,
    FocusTarget,
    GridController,
    RowAccess,
    ScrollTarget,
} from "./gridControllerTypes";
import { scrollCellIntoViewInline } from "./scrollIntoView";

export type {
    DataGridScrollAlign,
    DragSource,
    FocusQuiet,
    FocusTarget,
    GridController,
    RenderedLayout,
    RowBounds,
    ScrollTarget,
    SelectionRun,
} from "./gridControllerTypes";

/** Child rows mounting this soon after an expand animate in. */
const EXPAND_FADE_WINDOW_MS = 300;

const cellKey = (rowId: string, columnId: string) =>
    `${rowId}\u0000${columnId}`;

/** Row access with no Body mounted: lookups miss, so scroll requests wait for the next Body. */
const NO_ROW_ACCESS: RowAccess = {
    rowIndexById: new Map(),
    scrollToRowIndex: () => undefined,
    getRowIndexAtOffset: () => -1,
    getRowBounds: () => undefined,
};

/** A re-render or a control removed focus without moving it anywhere. */
export const isFocusLost = () =>
    document.activeElement === null || document.activeElement === document.body;

/** Focuses a row or cell, or the one control a cell hands focus to. */
const focusElement = (element: HTMLElement) => {
    (getSoleControl(element) ?? element).focus({ preventScroll: true });
};

export const createGridController = (initialWidth: number): GridController => {
    const rowElements = new Map<string, HTMLElement>();
    const cellElements = new Map<string, HTMLElement>();
    /** A focus request waiting for its element, and where focus was when it was made. */
    let pending: { target: FocusTarget; activeElement: Element | null } | null =
        null;
    let pendingScroll: (ScrollTarget & { isRowScrolled: boolean }) | null =
        null;
    let lastExpandedAt = Number.NEGATIVE_INFINITY;
    let fadeSkippedAt = Number.NEGATIVE_INFINITY;

    const getElement = (target: FocusTarget) =>
        target.columnId === null
            ? rowElements.get(target.rowId)
            : cellElements.get(cellKey(target.rowId, target.columnId));

    // Row-mode cells aren't registered; find them in the DOM.
    const queryCell = ({
        rowId,
        columnId,
    }: {
        rowId: string;
        columnId: string;
    }) =>
        controller.root?.querySelector<HTMLElement>(
            `[data-row-id="${CSS.escape(rowId)}"] [role="gridcell"][data-column-id="${CSS.escape(columnId)}"]`,
        ) ?? undefined;

    const scrollIntoViewInline = (cell: HTMLElement) => {
        if (controller.viewport) {
            scrollCellIntoViewInline({
                viewport: controller.viewport,
                cell,
                startInset: controller.layout.pinnedStartWidth,
                endInset: controller.layout.pinnedEndWidth,
            });
        }
    };

    const flushPendingScroll = () => {
        if (!pendingScroll) {
            return;
        }
        const { rowId, columnId, align } = pendingScroll;
        const index = controller.rowIndexById.get(rowId);
        if (!pendingScroll.isRowScrolled && index !== undefined) {
            pendingScroll.isRowScrolled = true;
            controller.scrollToRowIndex(index, align);
        }
        if (columnId === null) {
            pendingScroll = pendingScroll.isRowScrolled ? null : pendingScroll;
            return;
        }
        const cell =
            getElement({ rowId, columnId }) ?? queryCell({ rowId, columnId });
        if (cell) {
            pendingScroll = null;
            scrollIntoViewInline(cell);
        }
    };

    const controller: GridController = {
        viewport: null,
        root: null,
        containerWidth: createAtom(initialWidth),
        layout: { widths: {}, pinnedStartWidth: 0, pinnedEndWidth: 0 },
        ...NO_ROW_ACCESS,
        isFocusWithin: createAtom(false),
        focusSearch: null,
        registerRow: (rowId, element) => {
            if (element) {
                rowElements.set(rowId, element);
            } else {
                rowElements.delete(rowId);
            }
        },
        registerCell: ({ rowId, columnId }, element) => {
            const key = cellKey(rowId, columnId);
            if (element) {
                cellElements.set(key, element);
            } else {
                cellElements.delete(key);
            }
        },
        getElement,
        focusTarget: (target) => {
            pending = { target, activeElement: document.activeElement };
            controller.setFocusQuiet(null);
            const index = controller.rowIndexById.get(target.rowId);
            if (index !== undefined) {
                controller.scrollToRowIndex(index);
            }
            controller.flushPendingFocus();
        },
        returnFocus: (target) => {
            requestAnimationFrame(() => {
                const element = getElement(target);
                if (
                    element &&
                    (isFocusLost() || element.contains(document.activeElement))
                ) {
                    focusElement(element);
                }
            });
        },
        hasPendingFocus: () => pending !== null,
        getFocusQuiet: () => {
            const quiet = controller.root?.dataset.focusQuiet;
            return quiet === "all" || quiet === "ring" ? quiet : null;
        },
        setFocusQuiet: (quiet) => {
            if (!controller.root) {
                return;
            }
            if (quiet === null) {
                delete controller.root.dataset.focusQuiet;
            } else {
                controller.root.dataset.focusQuiet = quiet;
            }
        },
        setFocusLevel: (level) => {
            if (
                controller.root &&
                controller.root.dataset.focusLevel !== level
            ) {
                controller.root.dataset.focusLevel = level;
            }
        },
        scrollTo: (target) => {
            pendingScroll = { ...target, isRowScrolled: false };
            controller.flushPendingFocus();
        },
        flushPendingFocus: () => {
            flushPendingScroll();
            if (!pending) {
                return;
            }
            const element = getElement(pending.target);
            if (!element) {
                return;
            }
            const isCell = pending.target.columnId !== null;
            const { activeElement } = pending;
            pending = null;
            // A user who moved on to type somewhere else keeps that field.
            const isTypingElsewhere =
                document.activeElement !== activeElement &&
                controller.root?.contains(document.activeElement) !== true &&
                isEditableTarget(document.activeElement);
            if (isTypingElsewhere) {
                return;
            }
            focusElement(element);
            if (isCell) {
                scrollIntoViewInline(element);
            }
        },
        restoreFocus: (target) => {
            if (target && controller.isFocusWithin.get() && isFocusLost()) {
                const element = getElement(target);
                if (element) {
                    focusElement(element);
                }
            }
        },
        markExpanded: () => {
            lastExpandedAt = performance.now();
        },
        skipExpandFade: () => {
            fadeSkippedAt = performance.now();
        },
        isJustExpanded: () => {
            const now = performance.now();
            return (
                now - lastExpandedAt < EXPAND_FADE_WINDOW_MS &&
                now - fadeSkippedAt >= EXPAND_FADE_WINDOW_MS
            );
        },
        body: null,
        setBody: (element) => {
            controller.body = element;
        },
        setViewport: (element) => {
            controller.viewport = element;
        },
        setRoot: (element) => {
            controller.root = element;
        },
        setFocusSearch: (focusSearch) => {
            controller.focusSearch = focusSearch;
        },
        setLayout: (layout) => {
            controller.layout = layout;
        },
        setRowAccess: (access) => {
            Object.assign(controller, access ?? NO_ROW_ACCESS);
        },
        dragSource: createAtom<DragSource | null>(null),
        selectionRun: null,
        rowMotion: createRowMotionTracker(),
    };
    return controller;
};
